import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getGame } from '../../data/games';
import { NotFound } from '../NotFound/NotFound';
import '../Game/Game.css';
import './Presskit.css';

export const Presskit: React.FC = () => {
  const { slug } = useParams();
  const game = getGame(slug);

  useEffect(() => {
    if (game) document.title = `${game.title} Press Kit | Practical Media`;
  }, [game]);

  if (!game || !game.presskit) return <NotFound />;

  const pk = game.presskit;
  const accentStyle = { ['--game-accent' as string]: game.accent } as React.CSSProperties;
  const primary = game.links.find((l) => l.primary);

  return (
    <div className="container game page presskit" style={accentStyle}>
      {/* Header */}
      <header className="game-head">
        <p className="label game-studio">
          {game.studioLogo && <img className="game-studio-logo" src={game.studioLogo} alt={`${game.studio} logo`} />}
          Press Kit · {game.studio}
        </p>
        <h1 className="game-title">
          {game.title}
          {game.subtitle && <span className="game-subtitle">{game.subtitle}</span>}
        </h1>
        <p className="game-tagline">{pk.shortDescription}</p>
        <div className="cta-row game-head-cta">
          {primary && (
            <a href={primary.href} target="_blank" rel="noopener noreferrer" className="primary-btn game-btn">
              {primary.label}
            </a>
          )}
          <a href={`mailto:${pk.pressContact}`} className="accent-link">{pk.pressContact}</a>
          <Link to={`/games/${game.slug}`} className="accent-link">← Game page</Link>
        </div>
      </header>

      {/* Fact sheet */}
      <section className="game-block">
        <p className="label">Fact sheet</p>
        <dl className="press-facts">
          {pk.factSheet.map((f) => (
            <div key={f.label}>
              <dt>{f.label}</dt>
              <dd>{f.value}</dd>
            </div>
          ))}
        </dl>
      </section>

      {/* Trailer */}
      {game.youtubeId && (
        <section className="game-block">
          <p className="label">Trailer</p>
          <div className="game-video">
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${game.youtubeId}`}
              title={`${game.title} trailer`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </section>
      )}

      {/* Description */}
      <section className="game-block game-pitch">
        <p className="label">Description</p>
        {pk.longDescription.map((p, i) => (
          <p key={i} className="body-text">{p}</p>
        ))}
      </section>

      {/* Features */}
      <section className="game-block">
        <p className="label">Features</p>
        <ul className="mono-list">
          {game.features.map((f) => <li key={f}>{f}</li>)}
        </ul>
      </section>

      {/* Asset usage terms set by the developer */}
      {pk.usageNote && (
        <section className="game-block">
          <p className="label">Using these assets</p>
          <p className="body-text press-usage">{pk.usageNote}</p>
        </section>
      )}

      {/* Screenshots — downloadable */}
      {pk.screenshots.length > 0 && (
        <section className="game-block">
          <p className="label">Screenshots <span className="press-hint">(click to download)</span></p>
          <div className="shots">
            {pk.screenshots.map((src, i) => (
              <a key={src} className="shot" href={src} download aria-label={`Download screenshot ${i + 1}`}>
                <img src={src} alt={`${game.title} screenshot ${i + 1}`} loading="lazy" />
              </a>
            ))}
          </div>
        </section>
      )}

      {/* Logos & key art — downloadable */}
      {pk.assets.length > 0 && (
        <section className="game-block">
          <p className="label">Logos &amp; key art <span className="press-hint">(click to download)</span></p>
          <div className="press-assets">
            {pk.assets.map((a) => (
              <a key={a.src} className="press-asset" href={a.src} download>
                <div className="press-asset-thumb">
                  <img src={a.src} alt={a.label} loading="lazy" />
                </div>
                <div className="press-asset-meta">
                  <span className="press-asset-label">{a.label}</span>
                  {a.note && <span className="press-asset-note">{a.note}</span>}
                </div>
              </a>
            ))}
          </div>
        </section>
      )}

      {/* Characters */}
      {game.characters && game.characters.length > 0 && (
        <section className="game-block">
          <p className="label">Characters</p>
          <div className="cast">
            {game.characters.map((c) => (
              <figure key={c.name} className="cast-card">
                <div className="cast-portrait">
                  <img src={c.image} alt={c.name} loading="lazy" />
                </div>
                <figcaption>
                  <p className="cast-name">{c.name}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>
      )}

      {/* About the developer */}
      <section className="game-block">
        <p className="label">About {game.studio}</p>
        <p className="body-text">{pk.aboutDeveloper}</p>
      </section>

      {/* Links & contact */}
      <section className="game-block">
        <p className="label">Links &amp; contact</p>
        <ul className="mono-list">
          {primary && (
            <li><a href={primary.href} target="_blank" rel="noopener noreferrer">{primary.href}</a></li>
          )}
          {game.socials?.map((s) => (
            <li key={s.href}>
              {s.label}: <a href={s.href} target="_blank" rel="noopener noreferrer nofollow">{s.href}</a>
            </li>
          ))}
          <li>Press: <a href={`mailto:${pk.pressContact}`}>{pk.pressContact}</a></li>
          <li>Published by <Link to="/about">Practical Media</Link></li>
        </ul>
      </section>
    </div>
  );
};
